import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";

/**
 * `/community` — aggregator page for Discord + Reddit.
 *
 * Deliberately copy-free — the artist will provide her own voice for any
 * headers, intros, or descriptions. Until then this page is structural
 * only: title placeholder + two platform cards. New platforms get added
 * by extending PLATFORMS.
 */

const PLATFORMS = [
  {
    key: "discord",
    name: "Discord",
    url: "",   // empty → renders as "Coming soon" until artist supplies invite
    cta: "Join the server",
    accent: "#5865F2",
  },
  {
    key: "reddit",
    name: "Reddit",
    url: "",   // empty → renders as "Coming soon" until artist supplies URL
    cta: "Join the subreddit",
    accent: "#FF4500",
  },
];

function PlatformCard({ p }) {
  const ready = !!(p.url && p.url.trim());
  return (
    <a
      href={ready ? p.url : undefined}
      target={ready ? "_blank" : undefined}
      rel="noopener noreferrer"
      aria-disabled={!ready}
      onClick={(e) => !ready && e.preventDefault()}
      className={`group block border border-white/10 hover:border-[#f3e0a6]/40 bg-white/[0.02] hover:bg-white/[0.04] transition-all p-6 sm:p-8 relative ${
        ready ? "" : "opacity-55 cursor-not-allowed"
      }`}
      data-testid={`community-platform-${p.key}`}
    >
      <div className="flex items-center gap-2 mb-4">
        <span
          className="w-2 h-2 rounded-full"
          style={{ background: p.accent }}
          aria-hidden="true"
        />
        <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-white/55">
          {p.name}
        </span>
      </div>
      <div className="flex items-center justify-between gap-3 mt-10">
        <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-white/80 group-hover:text-[#f3e0a6]">
          {ready ? p.cta : "Coming soon"}
        </span>
        {ready && (
          <ExternalLink
            className="w-3.5 h-3.5 text-white/50 group-hover:text-[#f3e0a6] transition-colors"
            strokeWidth={1.5}
          />
        )}
      </div>
    </a>
  );
}

export default function Community() {
  return (
    <main
      className="min-h-screen bg-[var(--mm-bg,#050a1a)] text-white"
      data-testid="community-page"
    >
      <div className="max-w-3xl mx-auto px-6 sm:px-10 pt-24 sm:pt-32 pb-24">
        <h1
          className="deco-text text-5xl sm:text-6xl lg:text-7xl uppercase leading-[0.95] mb-16"
          style={{ letterSpacing: "0.08em" }}
          data-testid="community-title"
        >
          Community
        </h1>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {PLATFORMS.map((p) => (
            <PlatformCard p={p} key={p.key} />
          ))}
        </div>

        <p className="text-center mt-20 font-mono text-[10px] uppercase tracking-[0.32em] text-white/40">
          <Link to="/" className="hover:text-white/85">
            &larr; Back to Metamorph
          </Link>
        </p>
      </div>
    </main>
  );
}
